import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'

// ─── Class name helper ───────────────────────────────────────────────────────

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

// ─── Currency ────────────────────────────────────────────────────────────────

/**
 * Formats a numeric value as Brazilian Real.
 * e.g. 1500 → "R$ 1.500,00"
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// ─── Dates ───────────────────────────────────────────────────────────────────

/**
 * Formats a date string or Date object.
 * Default format: 'dd/MM/yyyy'
 */
export function formatDate(
  date: string | Date,
  dateFormat: string = 'dd/MM/yyyy',
): string {
  const parsed = typeof date === 'string' ? parseISO(date) : date
  return format(parsed, dateFormat, { locale: ptBR })
}

/**
 * Formats a date string or Date object with time.
 * e.g. "30/04/2026 às 14:30"
 */
export function formatDateTime(date: string | Date): string {
  return formatDate(date, "dd/MM/yyyy 'às' HH:mm")
}

// ─── String helpers ──────────────────────────────────────────────────────────

/**
 * Returns up to 2 initials from a full name.
 * e.g. "João Silva" → "JS" | "Ana" → "A"
 */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const first = words[0][0] ?? ''
  const second = words.length > 1 ? words[words.length - 1][0] ?? '' : ''
  return (first + second).toUpperCase()
}

/**
 * Converts a string to a URL-friendly slug.
 * e.g. "Direção Defensiva" → "direcao-defensiva"
 */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9\s-]/g, '')    // remove non-alphanumeric
    .replace(/\s+/g, '-')             // spaces → hyphens
    .replace(/-+/g, '-')              // collapse multiple hyphens
    .replace(/^-|-$/g, '')            // trim leading/trailing hyphens
}

// ─── Geo ─────────────────────────────────────────────────────────────────────

/**
 * Calculates the great-circle distance between two coordinates using the
 * Haversine formula. Returns distance in kilometres.
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371 // Earth's radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180

  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// ─── Platform fees ───────────────────────────────────────────────────────────

/** Returns the 15 % platform fee for a given booking amount. */
export function getPlatformFee(amount: number): number {
  return amount * 0.15
}

/** Returns the 85 % instructor payout for a given booking amount. */
export function getInstructorPayout(amount: number): number {
  return amount * 0.85
}

// ─── Rating ──────────────────────────────────────────────────────────────────

/** Formats a rating number to one decimal place. e.g. 4.833 → "4.8" */
export function formatRating(rating: number): string {
  return rating.toFixed(1)
}

// ─── Booking status ──────────────────────────────────────────────────────────

/**
 * Returns a human-readable Portuguese label for a booking status.
 */
export function getBookingStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmada',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled: 'Cancelada',
    no_show: 'Não compareceu',
  }
  return labels[status] ?? status
}

/**
 * Returns a Tailwind CSS color class suitable for a status badge.
 */
export function getBookingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-brand-100 text-brand-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    no_show: 'bg-gray-100 text-gray-800',
  }
  return colors[status] ?? 'bg-gray-100 text-gray-800'
}
